import _ from 'lodash'

/**
 * Filters nodes by their display name or type based on the search term.
 * @param {PaletteNode[]} nodes - The list of nodes to filter.
 * @param {string} searchTerm - The search term to filter nodes by.
 * @returns {PaletteNode[]} The filtered list of nodes.
 */
export const filterNodeByType = (nodes, searchTerm) => {
  if (!nodes || nodes.length === 0) {
    return []
  }
  if (!searchTerm || searchTerm.trim() === '') {
    return [...nodes]
  }

  return nodes.filter(i => {
    const nodeName = (i.displayName || i.name || '').toLowerCase()
    const nodeType = (i.type || '').toLowerCase()
    return nodeName.includes(searchTerm.toLowerCase()) || nodeType.includes(searchTerm.toLowerCase())
  })
}

/**
 * Filters nodes by their display name or any property based on the search term.
 * @param {PaletteNode[]} nodes - The list of nodes to filter.
 * @param {string} searchTerm - The search term to filter nodes by.
 * @returns {PaletteNode[]} The filtered list of nodes.
 */
export const filterNodeBySearchText = (nodes, searchText) => {
  if (!nodes || nodes.length === 0) {
    return []
  }
  if (!searchText || searchText.trim() === '') {
    return nodes
  }

  return _.filter(nodes, node => {
    return _.some(node, value => {
      return _.includes(_.toLower(value), _.toLower(searchText))
    })
  })
}

/**
 * Filters nodes based on available connections and search term.
 * @param {PaletteNode[]} nodes - The list of nodes to filter.
 * @param {string} searchTerm - The search term to filter nodes by.
 * @param {string[]} sourceNodeConnections - Available connections from the source node.
 * @returns {PaletteNode[]} The filtered list of nodes.
 */
export const filterNodeByTypeAndConnections = (nodes, searchTerm, sourceNodeConnections = []) => {
  // Cache lowercase search term outside the loop
  const searchLower = searchTerm?.toLowerCase() ?? ''
  const hasSearch = searchLower !== ''
  const hasConnections = sourceNodeConnections?.length > 0

  // Use Set for O(1) lookups instead of O(n) array includes
  const connectionsSet = hasConnections ? new Set(sourceNodeConnections) : null

  return nodes.filter(node => {
    // First check if the node matches the search term
    if (hasSearch) {
      const matchesSearch =
        (node.displayName || node.name || '').toLowerCase().includes(searchLower) ||
        (node.type || '').toLowerCase().includes(searchLower)
      if (!matchesSearch) {
        return false
      }
    }

    // If no source connections specified, allow all nodes
    if (!connectionsSet) {
      return true
    }

    // Check item_type, subtype, or type match using Set lookup
    return (
      connectionsSet.has(node.item_type) ||
      (node.subtype && connectionsSet.has(node.subtype)) ||
      connectionsSet.has(node.type)
    )
  })
}

/**
 * Filters sections by their items' display name or type based on the search term.
 * @param {PaletteSection[]} sections - The list of sections to filter.
 * @param {string} searchTerm - The search term to filter sections by.
 * @returns {PaletteSection[]} The filtered list of sections with items matching the search term.
 */
export const filterSectionByType = (sections, searchTerm) => {
  return sections
    .map(section => {
      return { ...section, items: filterNodeBySearchText(section.items, searchTerm) }
    })
    .filter(s => !!s.items.length)
}

/**
 * Filters sections by their items based on search term and available connections.
 * @param {PaletteSection[]} sections - The list of sections to filter.
 * @param {string} searchTerm - The search term to filter sections by.
 * @param {string[]} sourceNodeConnections - Available connections from the source node.
 * @returns {PaletteSection[]} The filtered list of sections with compatible items.
 */
export const filterSectionByTypeAndConnections = (sections, searchTerm, sourceNodeConnections = []) => {
  return sections
    .map(section => {
      return {
        ...section,
        items: filterNodeByTypeAndConnections(section.items, searchTerm, sourceNodeConnections)
      }
    })
    .filter(s => !!s.items.length)
}
